import type { AnyPlayActResult, AnyPlayView, PlayKind, PlayPrize, RunPot } from '@goodtime/shared';

/**
 * The shared shape of a turn-based mini-game screen (smash, match,
 * scratch, wheel): the player asks for an act, the server answers with what
 * it revealed and the state after, and the last answer ends the game with
 * its prize. One act is in flight at a time, so mashing sends one.
 */
export interface GameView<S, R> {
  /** `play`: waiting for the player; `waiting`: an act is with the
   *  server; `over`: the game has ended and paid. */
  phase: 'play' | 'waiting' | 'over';
  /** The next act's step. */
  step: number;
  state: S | null;
  /** What the last act revealed. */
  last: R | null;
  /** The piece being played (a tile, a card), while waiting. */
  busy: number | null;
  prize: PlayPrize | null;
  pot: RunPot | null;
  toPot: boolean;
  error: string | null;
}

export type GameEvent<S, R> =
  | { type: 'act'; action: string; arg?: string; piece?: number }
  | { type: 'answered'; result: AnyPlayActResult<R, S> }
  | { type: 'failed'; play: AnyPlayView<PlayKind, unknown, S> | null };

export type GameEffect = { type: 'act'; step: number; action: string; arg?: string } | null;

const coinsOnly = (coins: number): PlayPrize => ({ coins, gems: 0, freeSpins: 0 });

export function gameStart<S, R>(toPot: boolean): GameView<S, R> {
  return { phase: 'play', step: 0, state: null, last: null, busy: null, prize: null, pot: null, toPot, error: null };
}

export function reduceGame<S, R>(view: GameView<S, R>, event: GameEvent<S, R>): { view: GameView<S, R>; effect: GameEffect } {
  switch (event.type) {
    case 'act':
      if (view.phase !== 'play') return { view, effect: null };
      return {
        view: { ...view, phase: 'waiting', busy: event.piece ?? null, error: null },
        effect: { type: 'act', step: view.step, action: event.action, arg: event.arg },
      };
    case 'answered': {
      const r = event.result;
      const over = r.over;
      return {
        view: {
          ...view,
          phase: over ? 'over' : 'play',
          step: r.step + 1,
          state: r.state,
          last: r.reveal,
          busy: null,
          prize: over ? (r.prize ?? coinsOnly(r.payout ?? 0)) : null,
          pot: r.pot ?? null,
          error: null,
        },
        effect: null,
      };
    }
    case 'failed': {
      const p = event.play;
      if (!p) return { view: { ...view, phase: 'play', busy: null, error: 'Lost the connection. Try again.' }, effect: null };
      // Catch up with the server: where it is, and what it paid if it's over.
      const over = p.status === 'done' || p.status === 'expired';
      return {
        view: {
          ...view,
          phase: over ? 'over' : 'play',
          step: p.step,
          state: p.state ?? view.state,
          busy: null,
          prize: over ? (p.prize ?? coinsOnly(p.payout ?? 0)) : null,
          error: null,
        },
        effect: null,
      };
    }
  }
}

/** Where a prize went, under the amount: the run's pot for a lamp's game;
 *  otherwise coins to the wallet, and free spins to the slot's next spins
 *  (they never go in the wallet). A visitor's game raised to its floor says
 *  the coins are a thank-you, not a win. */
export function destination(toPot: boolean, prize?: PlayPrize | null): string {
  if (toPot) return 'Added to the pot';
  if (prize?.floored) return 'A thank-you for playing, in your wallet';
  const spins = (prize?.freeSpins ?? 0) > 0;
  const money = (prize?.coins ?? 0) > 0 || (prize?.gems ?? 0) > 0;
  if (spins && !money) return 'Your next spins are free';
  if (spins) return 'Coins to your wallet, and your next spins are free';
  return 'Added to your wallet';
}

/** A prize as words: "+75 coins", "+5 free spins", "+3 gems". */
export function prizeWords(prize: PlayPrize): string {
  const parts: string[] = [];
  if (prize.coins > 0) parts.push(`+${prize.coins.toLocaleString('en-GB')} coins`);
  if (prize.freeSpins > 0) parts.push(`+${prize.freeSpins} free spins`);
  if (prize.gems > 0) parts.push(`+${prize.gems} gems`);
  return parts.join(', ');
}
